import { getPeriodBoundsFromType, PeriodType } from '@/components/analysis-period-selector/analysis-period-selector';
import { ISO_FORMAT } from '@/interfaces/Date.interface';
import type { SystemId } from '@/interfaces/System.interface';
import type { UnitCode } from '@/interfaces/Unit.interface';
import { inject, Injectable } from '@angular/core';
import { createEntityAdapter, type EntityAdapter, type EntityState } from '@ngrx/entity';
import { TranslateService } from '@ngx-translate/core';
import moment, { type Moment } from 'moment';
import { firstValueFrom, type Observable } from 'rxjs';
import { AccessLevel } from '../../interfaces/Access.interface';
import type { BuildingId, Period } from '../../interfaces/Building.interface';
import type { VatModes } from '../budgets/budgets.constants';
import { BuildingsProvider } from '../buildings/buildings.provider';
import { API_DATE_FORMAT } from '../constants.api';
import { InformationProvider } from '../information/information.provider';
import { IpeStaticProvider } from '../ipe.static.provider';
import {
    LegacyGraphTileTimeStep,
    type AnalysisGraphTileData,
    type AnalysisGraphTileDescription,
    type AnalysisGraphTileDescriptionSensor,
    type CustomTileModel,
    type IpeHistoryTileData,
    type IpeTileData,
    type IpeTileUnitCode,
    type LegacyGraphTileSensor,
    type LegacyGraphTileTemplate,
    type MultiSensorTileCreationBody,
    type MultiTileModel,
    type TileCreationBody,
    type TileData,
    type TileRange
} from './../../interfaces/Tile.interface';
import { AdvizeoCoreTilesProvider } from './advizeo-core-tiles.provider';
import { TileConstants } from './tiles.constants';

// These methods have been moved directly from the old realtime analysis
// These could have probably been simplify ,think about it if necessary ^^'

export const idSplitter: string = '%';

function getPreviousPeriod(start: string, end: string, newStart?: string): Period {
    const currentPeriodStart = moment(start);
    const currentPeriodEnd = moment(end);

    const periodDeltaInDays = currentPeriodEnd.diff(currentPeriodStart, 'days');

    const previousPeriodStart = newStart ? moment(newStart) : currentPeriodStart.clone();
    if (!newStart) {
        previousPeriodStart.subtract(periodDeltaInDays + 1, 'days');
        previousPeriodStart.subtract((previousPeriodStart.day() - currentPeriodStart.day() + 7) % 7, 'days');
    }

    const previousPeriodEnd = previousPeriodStart.clone();
    previousPeriodEnd.add(periodDeltaInDays, 'days').endOf('day');

    return {
        start: previousPeriodStart.format(ISO_FORMAT),
        end: previousPeriodEnd.format(ISO_FORMAT),
        name: newStart ? PeriodType.CUSTOM : PeriodType.DEFAULT
    };
}

export function generateRealtimeAnalysisStateSensorKey(systemId: SystemId, sensorId: string): string {
    return `${systemId}${idSplitter}${sensorId}`;
}

export const legacyGraphTileSensorAdapter: EntityAdapter<LegacyGraphTileSensor> = createEntityAdapter({
    selectId: sensor => generateRealtimeAnalysisStateSensorKey(sensor.systemId, sensor.sensorId)
});

@Injectable({
    providedIn: 'root'
})
export class TilesProvider {
    private _buildingProvider = inject(BuildingsProvider);
    private _advizeoCoreTilesProvider = inject(AdvizeoCoreTilesProvider);
    private _translateService = inject(TranslateService);
    private _informationProvider = inject(InformationProvider);

    hasTileAdminOption$(buildingId: BuildingId): Observable<boolean> {
        return this._buildingProvider.hasMinimumBuildingAccess$(buildingId, AccessLevel.WRITE);
    }

    fetchBuildingTilesByGroup(
        buildingId: BuildingId,
        tilesGroup: string,
        dateStart: Moment,
        dateEnd: Moment,
        isVatIncl: boolean
    ): Promise<TileData[]> {
        return firstValueFrom(
            this._advizeoCoreTilesProvider.fetchBuildingTilesByGroup$(buildingId, tilesGroup, dateStart, dateEnd, isVatIncl)
        );
    }

    getDisplayedUnit(code: IpeTileUnitCode | string): string {
        if (IpeStaticProvider.unitTab[code as IpeTileUnitCode]) {
            return IpeStaticProvider.unitTab[code as IpeTileUnitCode];
        } else {
            return code;
        }
    }

    getDisplayedTypedUnit(code: IpeTileUnitCode | string, type: string, asHtml: boolean, vatMode: VatModes): string {
        let formatted = this.getDisplayedUnit(code);
        let sub;

        if (type === 'FINAL' || type === 'PRIMARY') {
            sub = this._translateService.instant(`IPE.${type}_SHORT`);
        } else if (type === 'CURRENCY') {
            sub = this._translateService.instant('STUFF.' + vatMode);
        }

        if (sub && asHtml) {
            formatted += `<sub>${sub}</sub>`;
        } else if (sub && !asHtml) {
            formatted += sub;
        }

        return formatted;
    }

    buildUnitWithTypeAndIndicatorToDisplay(
        indicator: string,
        unit: IpeTileUnitCode | string,
        energyType: string,
        customIndicatorLabel: string,
        asHtml: boolean,
        vatMode: VatModes
    ) {
        if (indicator === TileConstants.CONSTANT_1) {
            return this.getDisplayedTypedUnit(unit, energyType, asHtml, vatMode);
        } else if (indicator === TileConstants.CUSTOM_CONSTANT) {
            return this.getDisplayedTypedUnit(unit, energyType, asHtml, vatMode) + ' / ' + customIndicatorLabel;
        } else {
            return (
                this.getDisplayedTypedUnit(unit, energyType, asHtml, vatMode) +
                ' / ' +
                this._translateService.instant(`IPE.${indicator}_UNIT`)
            );
        }
    }

    getUnitWithTypeAndIndicatorToDisplay(tile: IpeTileData | IpeHistoryTileData, asHtml: boolean, vatMode: VatModes) {
        return this.buildUnitWithTypeAndIndicatorToDisplay(
            tile.description.indicator,
            tile.description.unit,
            tile.description.energyType,
            tile.description.customIndicator ? tile.description.customIndicator.label : (null as unknown as string),
            asHtml,
            vatMode
        );
    }

    createTile(tile: any): Promise<string> {
        return new Promise((resolve, reject) => {
            firstValueFrom(this._advizeoCoreTilesProvider.createTile$(tile))
                .then(result => {
                    resolve(result);
                })
                .catch(e => {
                    reject(e);
                });
        });
    }

    updateTile(id: string, tile: any): Promise<any> {
        return new Promise((resolve, reject) => {
            firstValueFrom(this._advizeoCoreTilesProvider.updateTile$(id, tile))
                .then(result => {
                    resolve(result);
                })
                .catch(e => {
                    reject(e);
                });
        });
    }

    renameTile(tileId: string, newName: string): Promise<void> {
        return new Promise(resolve => {
            firstValueFrom(this._advizeoCoreTilesProvider.renameTile$(tileId, newName))
                .then(() => {
                    this._informationProvider.showToast(this._translateService.instant('WIDGET.HOME_TILES.RENAME_CONFIRMATION'), 'success');
                    resolve();
                })
                .catch(err => {
                    this._informationProvider.showToast(this._translateService.instant('WIDGET.HOME_TILES.RENAME_ERROR'), 'error');
                    console.error(err);
                });
        });
    }

    getTilePreview(preview: any, dateStart: moment.Moment, dateEnd: moment.Moment): Promise<any> {
        return new Promise((resolve, reject) => {
            firstValueFrom(
                this._advizeoCoreTilesProvider.getIpeTilePreview$(
                    preview,
                    dateStart.format(API_DATE_FORMAT),
                    dateEnd.format(API_DATE_FORMAT)
                )
            )
                .then(result => {
                    resolve(result);
                })
                .catch(e => {
                    reject(e);
                });
        });
    }

    deleteTile(tileId: string, buildingId: BuildingId): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            firstValueFrom(this._advizeoCoreTilesProvider.deleteTile$(tileId, buildingId))
                .then(() => resolve())
                .catch(e => reject(e));
        });
    }

    // Use where ranges are not custom (tile.description.customRange = false)
    getRangeDescription(tile: IpeTileData) {
        if (tile.description.yearsRanges && tile.description.ranges && tile.description.ranges.length === 3) {
            // sort ranges
            const index = tile.description.ranges
                .sort((a: TileRange, b: TileRange) => a.start - b.start)
                .findIndex(range => range.start <= tile.value && tile.value <= range.end);
            switch (index) {
                case 0: {
                    return this._translateService.instant('IPE.RANGES_DESCRIPTION.BELOW_THE_OBJECTIVE', {
                        targetYearRatio: tile.description.targetYearRatio
                    });
                }
                case 1: {
                    return this._translateService.instant('IPE.RANGES_DESCRIPTION.BETWEEN_REFERENCE_YEAR_AND_GOAL', {
                        targetYearRatio: tile.description.targetYearRatio,
                        targetYear: tile.description.targetYear
                    });
                }
                case 2: {
                    return this._translateService.instant('IPE.RANGES_DESCRIPTION.HIGHER_THAN_THE_REFERENCE_YEAR', {
                        referenceYear: tile.description.referenceYear
                    });
                }
                default:
                    return '';
            }
        }
        return '';
    }

    createCustomTile(customTile: CustomTileModel, tileGroup: string): Promise<string> {
        const body: TileCreationBody = {
            buildingId: customTile.buildingId,
            tileGroup,
            name: customTile.name,
            type: customTile.type
        };

        switch (customTile.type) {
            case TileConstants.customTiles.MULTI_SENSORS:
            case TileConstants.customTiles.MULTI_STATE_SENSORS:
                return this.createMultiTile(customTile as MultiTileModel, body);
            default:
                return Promise.reject(`Unable to create a tile of the requested type: ${customTile.type}`);
        }
    }

    createMultiTile(tileModel: MultiTileModel, body: TileCreationBody): Promise<string> {
        return new Promise((resolve, reject) => {
            const multiSensorBody: MultiSensorTileCreationBody = {
                ...body,
                sensors: tileModel.sensors
            };

            firstValueFrom(this._advizeoCoreTilesProvider.createMultiTile(multiSensorBody))
                .then((tileModelId: string) => {
                    tileModelId ? resolve(tileModelId) : reject();
                })
                .catch((err: Response) => reject(err));
        });
    }

    static generateStateLikeDataFromTileData(tileData: AnalysisGraphTileData): LegacyGraphTileTemplate {
        const sensorGroups: { groupId: number; labelToTranslate: string; unitCode: UnitCode | null }[] = [
            { groupId: 1, labelToTranslate: '1', unitCode: null },
            { groupId: 2, labelToTranslate: '2', unitCode: null },
            { groupId: 3, labelToTranslate: '3', unitCode: null },
            { groupId: 4, labelToTranslate: '4', unitCode: null }
        ];
        const sensors: EntityState<LegacyGraphTileSensor> = legacyGraphTileSensorAdapter.setAll(
            tileData.description.sensors.map(sensor => ({
                colorHex: sensor.plotColorHex,
                displayUnitCode: sensor.displayUnitCode,
                plotType: sensor.plotType,
                sensorId: sensor.sensorId,
                systemId: sensor.systemId,
                timeseriesId: sensor.timeseriesId,
                universeId: sensor.universeId,
                previousPeriod: sensor.isPreviousPeriodActive,
                previousPeriodColorHex: sensor.plotPreviousPeriodColorHex,
                groupId: sensor.groupId
            })),
            legacyGraphTileSensorAdapter.getInitialState()
        );

        legacyGraphTileSensorAdapter
            .getSelectors()
            .selectAll(sensors)
            .forEach(sensor => {
                if (sensor.groupId) {
                    const index = sensorGroups.findIndex(sg => sg.groupId === sensor.groupId);
                    if (index !== -1 && !sensorGroups[index].unitCode) {
                        sensorGroups[index].unitCode = sensor.displayUnitCode;
                    }
                }
            });

        const currentPeriod: Period = {
            name: tileData.description.periodCode,
            ...getPeriodBoundsFromType(
                tileData.description.periodCode as PeriodType,
                tileData.description.startDate,
                tileData.description.endDate
            )
        };

        let timeStep: LegacyGraphTileTimeStep;
        if (tileData.description.timeStep === 'h') {
            timeStep = LegacyGraphTileTimeStep.HOUR;
        } else {
            timeStep = tileData.description.timeStep as LegacyGraphTileTimeStep;
        }

        return {
            templateId: null,
            templateName: tileData.name,
            buildingId: tileData.buildingId,
            weather: tileData.description.isWeatherActive,
            previousWeather: tileData.description.isWeatherPreviousPeriodActive,
            sensorGroups,
            axisEnabled: tileData.description.areAxesActive,
            timeStep,
            periods: {
                current: currentPeriod,
                previous: getPreviousPeriod(currentPeriod.start, currentPeriod.end, tileData.description.previousPeriodStartDate)
            },
            sensors
        };
    }

    static generateTileDataLikeDataFromState(state: LegacyGraphTileTemplate): AnalysisGraphTileData {
        const sensors: AnalysisGraphTileDescriptionSensor[] = [];
        Object.values(state.sensors.entities).forEach(sensor => {
            if (sensor) {
                sensors.push({
                    plotColorHex: sensor.colorHex,
                    displayUnitCode: sensor.displayUnitCode as UnitCode,
                    plotType: sensor.plotType,
                    sensorId: sensor.sensorId,
                    systemId: sensor.systemId,
                    timeseriesId: sensor.timeseriesId,
                    universeId: sensor.universeId,
                    isPreviousPeriodActive: sensor.previousPeriod,
                    plotPreviousPeriodColorHex: sensor.previousPeriodColorHex,
                    groupId: sensor.groupId as number
                });
            }
        });

        const description: AnalysisGraphTileDescription = {
            areAxesActive: state.axisEnabled,
            displayParameters: null,
            startDate: state.periods.current.start,
            endDate: state.periods.current.end,
            previousPeriodStartDate:
                state.periods.previous.name === PeriodType.CUSTOM ? state.periods.previous.start : (null as unknown as string),
            timeStep: state.timeStep,
            isWeatherActive: state.weather,
            isWeatherPreviousPeriodActive: state.previousWeather,
            periodCode: state.periods.current.name,
            sensors
        };

        return {
            tileId: null as unknown as string,
            name: null as unknown as string,
            tileType: TileConstants.customTiles.ANALYSIS_GRAPH,
            tileGroup: null as unknown as string,
            buildingId: state.buildingId as string,
            description,
            value: null as unknown as void,
            error: null as unknown as string
        };
    }
}
